import { futureSummary, historySummary, measured } from "./LegacyteamAnalysis";
import { comparableHistory, contextualMetrics } from "./teamWorkspace";
export const decisionSources = [
  "real",
  "result",
  "statistics",
  "should",
  "market",
];
export const decisionLabels = {
  real: ["Reale Ergebnisse · Saison", "Actual results · season"],
  result: [
    "Ergebnismodell · historische Erwartung",
    "Results model · historical expectation",
  ],
  statistics: [
    "Statistikmodell · historische Erwartung",
    "Statistics model · historical expectation",
  ],
  should: [
    "Should Goals · gemessene Leistung",
    "Should Goals · measured performance",
  ],
  market: ["Historische Marktrückrechnung", "Historical market inversion"],
  previous: [
    "Vorsaison · reale Ergebnisse",
    "Previous season · actual results",
  ],
  recent: ["Jüngste reale Ergebnisse", "Recent actual results"],
};
export const decisionOrigins = {
  real: ["Gespeicherte Endergebnisse", "Stored final scores"],
  result: [
    "Gespeicherte Ergebnisprognosen, keine Neuberechnung",
    "Stored results forecasts; not recalculated",
  ],
  statistics: [
    "Gespeicherte historische Statistikprognosen; Herkunft je Spiel",
    "Stored historical statistics forecasts; provenance per match",
  ],
  should: [
    "Gespeicherte Should Goals aus Spielstatistiken; keine Statistikmodell-Prognose",
    "Stored Should Goals derived from match statistics; not statistics-model forecasts",
  ],
  market: [
    "Gespeicherte Rückrechnung aus Marktquoten",
    "Stored inversion of market odds",
  ],
  previous: [
    "Archivierte tatsächliche Saisonbilanz",
    "Archived actual season record",
  ],
  recent: [
    "Endergebnisse der ausgewählten letzten Spiele",
    "Final scores of the selected recent matches",
  ],
};
const settingEvidence = (r) => {
  const s = futureSummary(r);
  return s
    ? Object.fromEntries(
        [
          "total_strength",
          "total_goals",
          "for_home",
          "against_home",
          "for_away",
          "against_away",
          "for_total",
          "against_total",
          "for_homefactor",
          "against_homefactor",
          "draw_factor",
        ]
          .filter((k) => measured(s[k]))
          .map((k) => [k, s[k]])
      )
    : null;
};
export function teamDecisionEvidence(
  data,
  id,
  {
    model = "result",
    metric = "total_strength",
    venue = "all",
    recent = 8,
  } = {}
) {
  const pool = comparableHistory(data.observations || [], id, ["real"], {
    venue,
  }).selected;
  const summarize = (source, games, key = source) => {
    const s = historySummary(games, id, source),
      used = new Set(s.rows.map((r) => r.id));
    const included = games.filter((g) => used.has(g.id));
    return {
      key,
      count: s.count,
      selected: games.length,
      missing: games
        .filter((g) => !used.has(g.id))
        .map((g) => ({
          id: g.id,
          date: g.kickoff,
          home: g.home_name,
          away: g.away_name,
        })),
      period: included.length
        ? `${included
            .at(-1)
            .kickoff.slice(0, 10)} – ${included[0].kickoff.slice(0, 10)}`
        : null,
      for: s.for,
      against: s.against,
      quotient: s.quotient,
      goal_sum: s.goal_sum,
      fixtures: included.map((g) => ({
        id: g.id,
        date: g.kickoff,
        home: g.home_name,
        away: g.away_name,
        source:
          source === "should"
            ? g.should?.source
            : source === "result"
            ? g.result?.calculation_timing || "timing_unknown"
            : source === "statistics"
            ? g.statistics_model?.kind
            : source === "market"
            ? g.market?.quality
            : null,
      })),
    };
  };
  const archive = contextualMetrics(data, id, "previous", venue, false);
  const rows = [
    ...decisionSources.map((s) => summarize(s, pool)),
    {
      key: "previous",
      count:
        measured(archive.goalsFor) && measured(archive.goalsAgainst)
          ? archive.count
          : 0,
      selected: archive.count,
      missing: [],
      period: archive.period || null,
      for: archive.goalsFor,
      against: archive.goalsAgainst,
      quotient: archive.quotient,
      goal_sum:
        archive.count > 0 && measured(archive.goalTotal)
          ? archive.goalTotal / archive.count
          : null,
      fixtures: [],
    },
    summarize("real", pool.slice(0, recent), "recent"),
  ];
  const paired = (a, b) => {
    const common = comparableHistory(data.observations || [], id, [a, b], {
      venue,
    }).common;
    const x = historySummary(common, id, a),
      y = historySummary(common, id, b);
    const delta = common.length
      ? (x.for - x.against - (y.for - y.against)) / common.length
      : null;
    return {
      sources: [a, b],
      count: common.length,
      delta_goal_difference_per_game: delta,
      left: {
        for: x.for,
        against: x.against,
        quotient: x.quotient,
        goal_sum: x.goal_sum,
      },
      right: {
        for: y.for,
        against: y.against,
        quotient: y.quotient,
        goal_sum: y.goal_sum,
      },
    };
  };
  return {
    team_id: Number(id),
    team_name:
      data.result_teams?.[id]?.team_name ||
      pool[0]?.[
        pool[0]?.home_team_id === Number(id) ? "home_name" : "away_name"
      ] ||
      null,
    model,
    metric,
    venue,
    recent,
    settings: {
      result: settingEvidence(data.result_teams?.[id]),
      statistics: settingEvidence(
        data.ranking?.find((r) => r.team_id === Number(id))
      ),
    },
    rows,
    performance: paired("should", model),
    outcome_variance: paired("real", "should"),
    schedule: pool.map((g) => ({
      id: g.id,
      venue: g.home_team_id === Number(id) ? "home" : "away",
      opponent: g.home_team_id === Number(id) ? g.away_name : g.home_name,
    })),
    opponent_adjusted_strength_available: false,
  };
}

// Field selection only; all edits still use the existing model routines.
export function decisionField(level, side, venue, metric) {
  return level === "summary"
    ? metric
    : `${side}_${level === "venue" ? venue : "total"}`;
}
export function decisionRowValue(row, field) {
  if (field === "total_strength") return row.quotient;
  if (field === "total_goals") return row.goal_sum;
  const value = field.startsWith("for_") ? row.for : row.against;
  return row.count > 0 && measured(value) ? value / row.count : null;
}
export function explainTeamDecision(
  e,
  { en = false, level = "summary", side = "for", overview = false } = {}
) {
  const t = (d, a) => (en ? a : d),
    fmt = (n) =>
      measured(n)
        ? n.toLocaleString(en ? "en-GB" : "de-DE", {
            minimumFractionDigits: 3,
            maximumFractionDigits: 3,
          })
        : "—";
  const field = decisionField(level, side, e.venue, e.metric),
    labels = (k) => decisionLabels[k][en ? 1 : 0],
    row = (k) => e.rows.find((r) => r.key === k),
    value = (k) => decisionRowValue(row(k), field);
  const model =
    e.model === "result"
      ? t("Ergebnismodell", "Results model")
      : t("Statistikmodell", "Statistics model");
  const name =
    level === "summary"
      ? e.metric === "total_strength"
        ? t("Spielstärke / Quotient", "Strength / ratio")
        : t("Torniveau / Toreschnitt", "Goal level / goals per game")
      : t(
          side === "for" ? "Für pro Spiel" : "Gegen pro Spiel",
          side === "for" ? "For per game" : "Against per game"
        );
  const saved = e.settings[e.model],
    other = e.settings[e.model === "result" ? "statistics" : "result"];
  const claims = [
    {
      title: overview
        ? t("So ist das Team gespeichert", "How the team is stored")
        : name,
      text: saved
        ? t(
            `${model}: ${name} ${fmt(saved[field])}. Das andere Modell: ${fmt(
              other?.[field]
            )}. Diese Werte gelten für künftige Berechnungen.`,
            `${model}: ${name} ${fmt(saved[field])}. Other model: ${fmt(
              other?.[field]
            )}. These settings apply to future calculations.`
          )
        : t(
            "Für das gewählte Modell fehlen persönliche Settings.",
            "Personal settings for the selected model are missing."
          ),
    },
  ];
  if (overview)
    claims[0].text = t(
      `Ergebnismodell: Quotient ${fmt(
        e.settings.result?.total_strength
      )}, Toreschnitt ${fmt(
        e.settings.result?.total_goals
      )}. Statistikmodell: Quotient ${fmt(
        e.settings.statistics?.total_strength
      )}, Toreschnitt ${fmt(
        e.settings.statistics?.total_goals
      )}. Das sind die gespeicherten Zukunftssettings, keine bisher erspielten Saisonwerte.`,
      `Results model: ratio ${fmt(
        e.settings.result?.total_strength
      )}, goals/game ${fmt(
        e.settings.result?.total_goals
      )}. Statistics model: ratio ${fmt(
        e.settings.statistics?.total_strength
      )}, goals/game ${fmt(
        e.settings.statistics?.total_goals
      )}. These are saved future settings, not season performance.`
    );
  if (saved)
    claims[0].text +=
      " " +
      t(
        `Zusammensetzung des gewählten Modells: Für ${fmt(
          saved.for_total
        )}, Gegen ${fmt(
          saved.against_total
        )}. Quotient = Für / Gegen; Toreschnitt = Für + Gegen.`,
        `Selected model composition: For ${fmt(saved.for_total)}, Against ${fmt(
          saved.against_total
        )}. Ratio = For / Against; goals/game = For + Against.`
      );
  const describe = (k) => {
    const r = row(k);
    return `${labels(k)}: ${fmt(value(k))} (${r.count}/${r.selected}; ${
      r.period || t("Zeitraum fehlt", "period missing")
    })`;
  };
  claims.push({
    title: t("Erwartet und eingetreten", "Expected and observed"),
    text:
      [
        describe("result"),
        describe("statistics"),
        describe("real"),
        describe("should"),
      ].join(". ") + ".",
  });
  claims.push({
    title: t(
      "Markt, Vorsaison und jüngste Spiele",
      "Market, previous season and recent matches"
    ),
    text:
      [describe("market"), describe("previous"), describe("recent")].join(
        ". "
      ) + ".",
  });
  const a = value("recent"),
    b = value("real");
  claims.push({
    title: t(
      "Saison und jüngste Spiele einordnen",
      "Putting season and recent matches in context"
    ),
    text:
      measured(a) && measured(b)
        ? t(
            `Die jüngsten ${row("recent").count} verfügbaren Spiele (Auswahl ${
              e.recent
            }) liegen bei ${fmt(a)}, die Saison bei ${fmt(
              b
            )}. Die jüngsten Spiele sind Teil der Saison, keine unabhängige Bestätigung. Unterschiedliche Gegner und Spielorte beeinflussen die Werte.`,
            `The latest ${row("recent").count} available matches (selection ${
              e.recent
            }) show ${fmt(a)}, the season ${fmt(
              b
            )}. Recent matches are part of the season, not independent corroboration. Different opponents and venues affect these values.`
          )
        : t(
            "Saison und jüngste Spiele sind mangels vollständiger Grundlagen nicht gemeinsam einzuordnen.",
            "Missing evidence prevents a season/recent comparison."
          ),
  });
  const p = e.performance,
    l = e.outcome_variance;
  claims.push({
    title: t(
      "Leistung und Ergebnis getrennt betrachten",
      "Separating performance from results"
    ),
    text:
      (p.count
        ? t(
            `Auf denselben ${
              p.count
            } Spielen liegt die Should-Tordifferenz um ${fmt(
              p.delta_goal_difference_per_game
            )} Tore pro Spiel gegenüber der historischen Erwartung des gewählten Modells.`,
            `On the same ${
              p.count
            } matches, Should goal difference differs by ${fmt(
              p.delta_goal_difference_per_game
            )} goals per game from the selected model's historical expectation.`
          )
        : t(
            "Für Leistung gegenüber Erwartung fehlen gemeinsame Spiele.",
            "No shared games for performance versus expectation."
          )) +
      " " +
      (l.count
        ? t(
            `Reale Tordifferenz minus Should-Tordifferenz: ${fmt(
              l.delta_goal_difference_per_game
            )} bei ${
              l.count
            } Spielen. Das beschreibt die Ergebnisabweichung, beweist aber weder Glück noch Pech.`,
            `Actual minus Should goal difference: ${fmt(
              l.delta_goal_difference_per_game
            )} across ${
              l.count
            } matches. This describes outcome variance, not proof of luck or misfortune.`
          )
        : t(
            "Für die Ergebnisabweichung gegenüber Should Goals fehlt die Grundlage.",
            "Evidence for outcome variance against Should Goals is missing."
          )),
  });
  const f = p.count ? (p.left.for - p.right.for) / p.count : null,
    g = p.count ? (p.left.against - p.right.against) / p.count : null;
  claims.push({
    title: t("Was sich gezielt prüfen lässt", "What to review next"),
    text: p.count
      ? t(
          `Should Goals gegenüber Modell-Erwartung, pro Spiel: Für ${fmt(
            f
          )}, Gegen ${fmt(
            g
          )}. Prüfe diese beiden Seiten getrennt in „Für / Gegen“ und anschließend „Heim / Auswärts“. Die Vorzeichen beschreiben nur die Abweichung; sie bestimmen keinen neuen Settingwert.`,
          `Should Goals versus model expectation, per game: For ${fmt(
            f
          )}, Against ${fmt(
            g
          )}. Review the two sides separately in “For / Against”, then “Home / Away”. Signs describe differences; they do not determine new settings.`
        )
      : t(
          "Ohne passende Leistungserwartung lässt sich keine datengestützte Änderungsrichtung ableiten. Die vorhandenen Settings bleiben manuell bearbeitbar.",
          "Without matching performance expectations, no evidence-based direction of change can be derived. Existing settings remain editable."
        ),
  });
  const reconstructed = e.rows
    .filter((r) => ["result", "statistics"].includes(r.key))
    .some((r) =>
      r.fixtures.some((f) =>
        /replay|reconstruct|postmatch/.test(f.source || "")
      )
    );
  const missing = e.rows
    .filter((r) => r.count < r.selected || !r.count)
    .map((r) => `${labels(r.key)}: ${r.count}/${r.selected}`);
  const cautions = [
    t(
      "Historische Quotienten sind nicht gegnerbereinigte Modellstärken. Ein direkter Zahlenunterschied rechtfertigt allein keine Anpassung.",
      "Historical ratios are not opponent-adjusted model strengths. A numerical difference alone does not justify a change."
    ),
    t(
      "Gesamtsettings gewichten Heim und Auswärts nach der bestehenden Modelllogik gleich. Historische Durchschnitte gewichten die tatsächlich ausgewählten Spiele.",
      "Overall settings weight home and away equally under the existing model rules. Historical averages weight the actual selected matches."
    ),
    t(
      "Vorsaison: eigener Zeitraum und gegebenenfalls andere Liga; deshalb kein unmittelbarer Stärkevergleich.",
      "Previous season: a separate period and possibly a different league, so not a direct strength comparison."
    ),
  ];
  if (reconstructed)
    cautions.push(
      t(
        "Ein Teil der gespeicherten historischen Erwartungen wurde nachträglich rekonstruiert und ist keine damalige Vorhersage.",
        "Some stored historical expectations were reconstructed later and are not contemporaneous predictions."
      )
    );
  if (row("market").fixtures.some((f) => f.source && f.source !== "verified"))
    cautions.push(
      t(
        "Ein Teil der Marktrückrechnung ist als ungeprüft markiert.",
        "Some market inversions are marked unverified."
      )
    );
  return {
    claims,
    missing,
    cautions,
    proposal: null,
    proposalReason: t(
      "Für die Größe einer automatischen Setting-Anpassung fehlt eine verbindliche Interpretationsregel. Deshalb kein erfundener Zielwert: Wert manuell bearbeiten und Auswirkungen in der Vorschau prüfen.",
      "No agreed interpretation rule determines the size of an automatic setting adjustment. No target is invented: edit manually and inspect the preview."
    ),
  };
}
